/**
 * HEIC fotky z iPhonu (nahrané do deal-photos před převodem na JPEG) Chrome
 * ani Edge v <img> nevykreslí. Portál je samostatný projekt, proto zjednodušená
 * kopie logiky z M_CRM `src/lib/heicImage.ts` + `src/lib/heicDisplay.ts`:
 * fotka se stáhne, dekóduje (Safari nativně, jinde `heic-to`) a vrátí se
 * `blob:` URL s JPEG. Převody běží po jednom, ať nedojde paměť.
 */
import { useEffect, useMemo, useRef, useState } from "react";

const HEIC_RE = /\.(heic|heif)$/i;
const MAX_DIMENSION_PX = 2560;

export function isHeicPath(path: string): boolean {
  return HEIC_RE.test(path);
}

let queue: Promise<unknown> = Promise.resolve();

async function decode(blob: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob);
  } catch {
    const { heicTo } = await import("heic-to");
    return heicTo({ blob, type: "bitmap" });
  }
}

async function toJpegUrl(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const bitmap = await decode(await res.blob());
  const scale = Math.min(1, MAX_DIMENSION_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  try {
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const jpeg = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9),
    );
    if (!jpeg) throw new Error("canvas.toBlob nevrátil obrázek");
    return URL.createObjectURL(jpeg);
  } finally {
    bitmap.close?.();
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * `name` (cesta ve storage) → URL k vykreslení. HEIC se převádí na pozadí;
 * dokud převod běží, hodnota je prázdná. Po novém podepsání URL se nepřevádí znovu.
 */
export function useDisplayablePhotoUrls(
  photos: { name: string; url: string }[],
): Record<string, string> {
  const [converted, setConverted] = useState<Record<string, string>>({});
  const started = useRef(new Set<string>());
  const created = useRef<string[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const blobUrls = created.current;
    return () => {
      mounted.current = false;
      for (const u of blobUrls) URL.revokeObjectURL(u);
    };
  }, []);

  useEffect(() => {
    for (const p of photos) {
      if (!isHeicPath(p.name) || started.current.has(p.name)) continue;
      started.current.add(p.name);
      const run = queue.then(() => (mounted.current ? toJpegUrl(p.url) : null));
      queue = run.catch(() => undefined);
      run.then(
        (u) => {
          if (!u) return;
          if (!mounted.current) {
            URL.revokeObjectURL(u);
            return;
          }
          created.current.push(u);
          setConverted((prev) => ({ ...prev, [p.name]: u }));
        },
        (err: unknown) => {
          // Aspoň původní URL – Safari ji vykreslí, jinde zůstane odkaz na soubor.
          console.warn("[heic] Foto konnte nicht konvertiert werden:", p.name, err);
          if (mounted.current) setConverted((prev) => ({ ...prev, [p.name]: p.url }));
        },
      );
    }
  }, [photos]);

  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const p of photos) out[p.name] = isHeicPath(p.name) ? (converted[p.name] ?? "") : p.url;
    return out;
  }, [photos, converted]);
}
